import {AuthenticatedSessionState} from "./AuthenticatedSessionWaiter";

/** What a private route may do with the identity the app is currently holding. */
export enum PrivateRouteAccess {
	/** The session backs the cached user: render the route. */
	Granted = "granted",
	/** The cached user may or may not be real; the auth listener has not answered yet. */
	Undecided = "undecided",
	/** Nothing here authorizes a private route: send the visitor to the public entry. */
	Denied = "denied",
}

/**
 * Whether a private route may act on the login state it is showing (ui#2210).
 *
 * `user.isLoggedIn` is restored from the `user.v2` blob in `localStorage`, so it describes a session
 * that existed at some point on this device — not one the api will answer for now. The publication
 * from {@link AuthenticatedSessionWaiter} is the evidence, and until it arrives the claim on its own
 * is not enough: an account the api refuses (an unverified password identity, a revoked or expired
 * session) otherwise gets the whole signed-in shell for one page load, until the session chunk loads
 * and corrects it.
 *
 * PENDING is deliberately NOT read as denied. The listener publishes Pending on every boot, so
 * denying there would bounce every genuinely signed-in visitor off their own deep link before the
 * session had a chance to answer — a worse outcome than the window this closes. The caller holds
 * instead, and the wait is bounded by the listener rather than by a timer, exactly as
 * `AuthenticatedSessionWaiter.wait()` bounds the imperative callers of the same publication.
 *
 * A visitor with NO cached user is denied straight away, including while Pending. That is the
 * behaviour this app has always had for a signed-out deep link, and holding it instead would make
 * every such visit wait out the session bootstrap before it could be redirected.
 */
export function privateRouteAccess(
	cachedUserIsLoggedIn: boolean,
	session: AuthenticatedSessionState,
): PrivateRouteAccess {
	if (!cachedUserIsLoggedIn) {
		return PrivateRouteAccess.Denied;
	}
	switch (session) {
		case AuthenticatedSessionState.Authenticated:
			return PrivateRouteAccess.Granted;
		case AuthenticatedSessionState.Pending:
			return PrivateRouteAccess.Undecided;
		case AuthenticatedSessionState.FailedOrAnonymous:
			return PrivateRouteAccess.Denied;
	}
}
